// Single source of truth for TopicContainer.layout / ExerciseTemplateContainer.layout
// - a free string both backends leave frontend-owned (same convention as
// TopicElement.type / exerciseBlockTypes.js). Shared between the courses
// module (TopicContainer) and the exercise-templates module
// (ExerciseTemplateContainer) since the layout concept itself - grid/wrap/
// table/horizontal-flex - is identical in both places, only what kind of
// block ends up inside differs.
//
// `table` deliberately reuses `grid`'s exact config shape (columns/gap) -
// it's the same positioning engine with a different CSS treatment (visible
// cell borders), not a distinct layout algorithm, so it also reuses grid's
// constructor controls verbatim rather than getting its own.
//
// Length-valued fields (gap/minItemWidth/itemWidth) are stored as CSS
// length strings ("16px"/"25%") rather than bare numbers, so an author can
// choose px or % per field - parseLength/formatLength/toCssLength below
// handle both that and the original bare-number shape (real containers
// created before this existed still have e.g. `gap: 16`) transparently, no
// data migration needed.

export const CONTAINER_LAYOUT_TYPES = {
    grid: {
        key: 'grid', label: 'Сітка',
        defaultConfig: { columns: 2, gap: '16px' },
    },
    table: {
        key: 'table', label: 'Таблиця',
        defaultConfig: { columns: 2, gap: '0px' },
    },
    wrap: {
        key: 'wrap', label: 'Обтікання',
        // 'stretch' matches this layout's original (pre-justify-option)
        // behavior - items grow to fill each row - so old configs saved
        // without a `justify` key still render identically.
        defaultConfig: { minItemWidth: '220px', gap: '16px', justify: 'stretch' },
    },
    flex_row: {
        key: 'flex_row', label: 'Ряд',
        // 'start' matches this layout's original behavior - items sit at
        // their natural size, no stretch - same backward-compat reasoning.
        defaultConfig: { gap: '16px', itemWidth: null, justify: 'start' },
    },
};

export const CONTAINER_LAYOUT_TYPE_LIST = Object.values(CONTAINER_LAYOUT_TYPES);

export function getContainerLayoutType(key) {
    return CONTAINER_LAYOUT_TYPES[key] || null;
}

export function safeParseContainerConfig(configString, layout) {
    const fallback = (getContainerLayoutType(layout) || CONTAINER_LAYOUT_TYPES.grid).defaultConfig;
    if (!configString) return fallback;
    try {
        const parsed = JSON.parse(configString);
        return parsed && typeof parsed === 'object' ? { ...fallback, ...parsed } : fallback;
    } catch (e) {
        return fallback;
    }
}

// --- length fields (gap / minItemWidth / itemWidth): px or % ---

export function parseLength(value, fallbackNumber = 0) {
    if (value === null || value === undefined || value === '') return { number: fallbackNumber, unit: 'px' };
    if (typeof value === 'number') return { number: value, unit: 'px' };
    const match = String(value).match(/^(-?[\d.]+)(px|%)?$/);
    if (!match) return { number: fallbackNumber, unit: 'px' };
    return { number: Number(match[1]), unit: match[2] || 'px' };
}

export function formatLength(number, unit) {
    return number + (unit || 'px');
}

// Used by ContainerLayout.jsx to turn a config value into an actual CSS
// custom property value - a bare number (legacy configs, or `columns`
// which is never a length at all) is treated as px, a string carries its
// own unit already.
export function toCssLength(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    return typeof value === 'number' ? value + 'px' : String(value);
}

// --- justify (wrap / flex_row only): how items sit within extra row/line
// space - "stretch" grows items to fill it (wrap's original behavior),
// the rest keep items at their natural size and position the group. ---

export const JUSTIFY_OPTIONS = [
    { key: 'stretch', label: 'На всю ширину' },
    { key: 'start', label: 'Ліворуч' },
    { key: 'center', label: 'По центру' },
    { key: 'end', label: 'Праворуч' },
    { key: 'space-between', label: 'Рівномірно' },
];

const JUSTIFY_CSS_VALUES = {
    stretch: 'flex-start',
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    'space-between': 'space-between',
};

export function getJustifyCssValue(justify) {
    return JUSTIFY_CSS_VALUES[justify] || 'flex-start';
}

export function defaultJustifyFor(layout) {
    return layout === 'wrap' ? 'stretch' : 'start';
}
